/**
 * Zentrale Kontierungs-Logik — Single Source of Truth für ALLE Modale, die eine
 * Kontierung erfassen oder übernehmen (MissingFieldsModal „Position ergänzen",
 * KontierungBulkModal „Kontierung für Auswahl", später weitere).
 *
 * Enthält:
 *  - die kanonischen Kontierungstypen (value + i18n-labelKey),
 *  - das SAP-Kontierungstyp-Mapping (KNTTP) für Adapter/SAP-Integration,
 *  - die dynamischen Feld-Sichtbarkeits- und Pflichtregeln je Typ.
 *
 * SAP-KNTTP-Mapping (bestätigt von Taras, 2026-06-26):
 *   kostenstelle  -> 'K'   (Kostenstelle)
 *   auftrag       -> 'F'   (Auftrag/Innenauftrag)
 *   material      -> ''    (Lagermaterial, KEINE Kontierung; kontiertes Material
 *                           wird stattdessen über K/F kontiert)
 * „Dienstleistung" wurde am 29.07. ENTFERNT (PSTYP-Klärung, docs/banf-use-cases.md UC4):
 * Dienstleistung ist KEIN Kontierungstyp, sondern ein Positionstyp (EBAN.PSTYP 9) und
 * braucht immer einen Kontierungs-Träger (K/F/P) — sie kommt nach der Novelis-Präsentation
 * als eigene, kombinierbare Dimension zurück.
 * Hinweis: KNTTP ist kundenspezifisch konfigurierbar — beim späteren Auslesen der
 * Kundendatenbank kommt ein tenant-Mapping davor (siehe field-mapping-registry.json
 * tenantOverrides). Dieser Default ist die technische Basis.
 */

export const KONTIERUNGSTYPEN = [
  { value: 'kostenstelle',   labelKey: 'kontierung.typKostenstelle',   sapKnttp: 'K' },
  { value: 'auftrag',        labelKey: 'kontierung.typAuftrag',        sapKnttp: 'F' },
  { value: 'material',       labelKey: 'kontierung.typMaterial',       sapKnttp: ''  },
];

/**
 * Gültige Kontierungstyp-Werte im Gesamtsystem (BANF-Formular kennt zusätzlich 'psp',
 * das in den Warenkorb-Modalen bewusst fehlt, weil dort das PSP-Element-Feld fehlt).
 * Altwerte wie 'dienstleistung' (aus Warenkorb/Favoriten-Bestand) werden beim Laden
 * auf 'kostenstelle' normalisiert — der banfMapper würde sie sonst hart abweisen.
 */
export const VALID_KONTIERUNGSTYPEN = ['kostenstelle', 'auftrag', 'psp', 'material'];

export function normalizeKontierungstyp(typ) {
  return VALID_KONTIERUNGSTYPEN.includes(typ) ? typ : 'kostenstelle';
}

export const KONTIERUNGSTYP_TO_KNTTP = KONTIERUNGSTYPEN.reduce((map, t) => {
  map[t.value] = t.sapKnttp;
  return map;
}, {});
// psp ist gültiger Typ (BANF-Formular), fehlt aber bewusst in der Modal-Liste oben —
// ohne diesen Eintrag würde sapKnttpFor('psp') '' liefern ("keine Kontierung") statt 'P'.
KONTIERUNGSTYP_TO_KNTTP.psp = 'P';

/** SAP-Kontierungstyp (KNTTP) für einen internen Kontierungstyp. */
export function sapKnttpFor(typ) {
  return KONTIERUNGSTYP_TO_KNTTP[typ] != null ? KONTIERUNGSTYP_TO_KNTTP[typ] : '';
}

/**
 * KNTTP-Kontext für die Sachkonto-Wertehilfe (gl_account, Kostenelement-Filter).
 * BEWUSST nicht sapKnttpFor(): `material` hat kein Sachkonto-Feld, Altwerte
 * (z. B. 'dienstleistung' aus Bestandsdaten) filtern wie `K`. Einzige zweite
 * Ausprägung: `auftrag` -> 'F'.
 */
export function glAccountKnttpFor(typ) {
  return typ === 'auftrag' ? 'F' : 'K';
}

/**
 * Ist ein Kontierungsfeld für den gewählten Typ sichtbar?
 * purchasing_org / plant / purchasing_group sind typ-unabhängig (immer sichtbar).
 */
export function isKontierungFieldVisible(typ, field) {
  switch (field) {
    case 'auftrag':     return typ === 'auftrag';
    case 'cost_center': return typ === 'kostenstelle' || typ === 'auftrag';
    case 'gl_account':  return typ !== 'material';
    default:            return true;
  }
}

/**
 * Ist ein Kontierungsfeld für den gewählten Typ Pflicht?
 * Nur dort relevant, wo Pflicht erzwungen wird (z. B. „Position ergänzen");
 * Bulk-Übernahme nutzt nur die Sichtbarkeit (leere Felder werden nicht überschrieben).
 */
export function isKontierungFieldRequired(typ, field) {
  switch (field) {
    case 'purchasing_org': return true;
    case 'cost_center':    return typ === 'kostenstelle' || typ === 'auftrag';
    case 'auftrag':        return typ === 'auftrag';
    case 'gl_account':     return typ !== 'material';
    default:               return false;
  }
}
